import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Filter } from 'lucide-react';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { formatCategoryTreeLabel } from '../utils/categoryI18n';
import { translateAttributeLabel, translateAttributeOption } from '../utils/attributeI18n';
import { FILTER_ALL } from '../constants/categories';
import { CategoryNode } from '../types';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useSearchParams } from 'react-router-dom';
import { CATEGORY_ATTRIBUTES } from '../constants/attributeSchemas';
import { DynamicCategoryIcon } from './DynamicCategoryIcon';

interface HierarchicalFilterProps {
  selectedCategory: string;
  selectedSubCategory?: string;
  selectedDetailCategory?: string;
  onCategoryChange: (main: string, sub?: string, detail?: string, leafId?: string) => void;
}

const HierarchicalFilter: React.FC<HierarchicalFilterProps> = ({
  selectedCategory,
  selectedSubCategory,
  selectedDetailCategory,
  onCategoryChange,
}) => {
  const { t, i18n: i18nFromHook } = useTranslation('common');
  const { categories } = useSiteSettings();
  const [counts, setCounts] = React.useState<Record<string, number>>({});
  const [totalActive, setTotalActive] = React.useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/stats`).then(res => {
      if (cancelled) return;
      const data = res.data?.data || res.data;
      if (data?.categoryCounts) {
        setCounts(data.categoryCounts);
      }
      // visibleTotal shares the exact visibility rules as categoryCounts, so the
      // all-categories badge stays consistent with the per-category badges.
      const total = data?.visibleTotal ?? data?.activeAuctions;
      if (total != null) {
        setTotalActive(total);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (selectedCategory && selectedCategory !== FILTER_ALL) initial.add(selectedCategory);
    if (selectedSubCategory) initial.add(selectedSubCategory);
    if (selectedDetailCategory) initial.add(selectedDetailCategory);
    return initial;
  });

  const toggleExpand = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCategorySelect = (main: string, sub?: string, detail?: string, leaf?: string) => {
    // When category changes, clear all attribute filters
    const newParams = new URLSearchParams(searchParams);
    Array.from(newParams.keys()).forEach(key => {
      if (key.startsWith('attr_')) newParams.delete(key);
    });
    setSearchParams(newParams);
    onCategoryChange(main, sub, detail, leaf);
  };

  const handleAttributeChange = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    const paramKey = `attr_${key}`;
    if (!value) {
      newParams.delete(paramKey);
    } else {
      newParams.set(paramKey, value);
    }
    setSearchParams(newParams);
  };

  /**
   * Helper to collect all attributes from the selected path to prevent duplication.
   */
  const getUnifiedAttributes = React.useCallback(() => {
    if (selectedCategory === FILTER_ALL) return [];
    
    const attrsMap = new Map<string, any>();
    
    const collectFromNode = (nodes: CategoryNode[], path: string[]) => {
      if (!path.length) return;
      const targetId = path[0];
      const node = nodes.find(n => n.id === targetId);
      if (!node) return;
      
      // Add node's dynamic attributes
      (node.attributes || []).forEach(attr => {
        attrsMap.set(attr.key, attr);
      });
      
      // Add hardcoded fallbacks
      (CATEGORY_ATTRIBUTES[node.id] || []).forEach(attr => {
        if (!attrsMap.has(attr.key)) {
          attrsMap.set(attr.key, attr);
        }
      });
      
      if (node.children) {
        collectFromNode(node.children, path.slice(1));
      }
    };

    const path = [selectedCategory, selectedSubCategory, selectedDetailCategory].filter(Boolean) as string[];
    collectFromNode(categories, path);
    
    return Array.from(attrsMap.values());
  }, [selectedCategory, selectedSubCategory, selectedDetailCategory, categories]);

  const activeAttributes = React.useMemo(() => getUnifiedAttributes(), [
    getUnifiedAttributes
  ]);

  const renderCategoryIcon = (cat: CategoryNode, iconSize = "w-4 h-4", containerSize = "w-7 h-7", bgColor?: string) => {
    const isActive =
      selectedCategory === cat.id ||
      (!!selectedSubCategory && selectedSubCategory === cat.id) ||
      (!!selectedDetailCategory && selectedDetailCategory === cat.id);

    return (
      <div className={`relative flex shrink-0 items-center justify-center rounded-lg transition-all duration-300 overflow-hidden border ${containerSize} ${
        isActive 
          ? 'bg-primary-500 border-primary-400 text-white shadow-lg shadow-primary-500/30 scale-110' 
          : `${bgColor || 'bg-white/40 dark:bg-slate-700/40'} border-slate-200/50 dark:border-slate-600/30 backdrop-blur-md group-hover:border-primary-400/50 group-hover:scale-105`
      }`}>
        <DynamicCategoryIcon category={cat} className={iconSize} isActive={isActive} />
      </div>
    );
  };


  return (
    <div className={`flex flex-col gap-2 p-2 ${i18nFromHook.language.startsWith('ar') ? 'rtl' : 'ltr'}`}>
      <div className="flex items-center gap-2 mb-4 px-2">
        <Filter className="w-5 h-5 text-primary-500" />
        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
          {t('home.filters.title')}
        </h2>
      </div>

      <div className="space-y-0.5">
        {/* All Categories Option */}
        <button
          onClick={() => handleCategorySelect(FILTER_ALL)}
          className={`w-full flex items-center justify-between p-2 rounded-xl transition-all duration-300 ${
            selectedCategory === FILTER_ALL
              ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/20'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          <div className="flex items-center gap-2">
             <div className={`flex items-center justify-center w-7 h-7 rounded-lg ${selectedCategory === FILTER_ALL ? 'bg-primary-400/30' : 'bg-slate-200 dark:bg-slate-700'}`}>
                <SparklesIcon className="w-4 h-4" />
             </div>
             <span className="font-bold text-[13px]">{t('home.filters.all')}</span>
          </div>
          {totalActive > 0 && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
              selectedCategory === FILTER_ALL ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'
            }`}>
              {totalActive}
            </span>
          )}
        </button>

        {/* Recursive Category Tree */}
        {categories.map((cat) => (
          <CategoryItem 
            key={cat.id} 
            node={cat} 
            level={0}
            selectedCategory={selectedCategory}
            selectedSubCategory={selectedSubCategory}
            selectedDetailCategory={selectedDetailCategory}
            handleCategorySelect={handleCategorySelect}
            expandedCategories={expandedCategories}
            toggleExpand={toggleExpand}
            t={t}
            renderCategoryIcon={renderCategoryIcon}
            counts={counts}
          />
        ))}

        {/* Unified Smart Filters Section */}
        {activeAttributes.length > 0 && (
          <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800 animate-slideDown">
            <div className="flex items-center justify-between mb-4 px-2">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                  <Filter className="w-3.5 h-3.5 text-primary-500" />
                </div>
                <h3 className="text-xs font-bold text-slate-800 dark:text-slate-100 uppercase tracking-widest">
                  {t('home.filters.smartFilters')}
                </h3>
              </div>
              {Array.from(searchParams.keys()).some(k => k.startsWith('attr_')) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = new URLSearchParams(searchParams);
                    Array.from(next.keys()).forEach(k => { if (k.startsWith('attr_')) next.delete(k); });
                    setSearchParams(next);
                  }}
                  className="text-[10px] font-bold text-primary-500 hover:text-primary-600 transition-colors bg-primary-50 dark:bg-primary-900/20 px-2 py-1 rounded-md"
                >
                  {t('home.filters.clearAll')}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 px-2">
              {activeAttributes.map((attr) => (
                <div key={attr.key} className="space-y-1.5 group">
                  <label className="block text-[10px] font-bold text-slate-400 group-hover:text-primary-500 transition-colors">
                    {translateAttributeLabel(attr.key, attr.label, t)}
                  </label>
                  {attr.type === 'select' ? (
                    <div className="relative">
                      <select
                        value={searchParams.get(`attr_${attr.key}`) || ''}
                        title={translateAttributeLabel(attr.key, attr.label, t)}
                        onChange={(e) => handleAttributeChange(attr.key, e.target.value)}
                        className="w-full appearance-none bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5 text-xs text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all shadow-sm"
                      >
                        <option value="">{t('home.filters.any')}</option>
                        {attr.options?.map((opt: string) => (
                          <option key={opt} value={opt}>{translateAttributeOption(attr.key, opt, t)}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                  ) : (
                    <input
                      type={attr.type === 'number' || attr.type === 'year' ? 'number' : 'text'}
                      value={searchParams.get(`attr_${attr.key}`) || ''}
                      onChange={(e) => handleAttributeChange(attr.key, e.target.value)}
                      placeholder={t('home.filters.any')}
                      className="w-full bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5 text-xs text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all shadow-sm"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface CategoryItemProps {
  node: CategoryNode;
  level: number;
  selectedCategory: string;
  selectedSubCategory?: string;
  selectedDetailCategory?: string;
  handleCategorySelect: (main: string, sub?: string, detail?: string, leafId?: string) => void;
  expandedCategories: Set<string>;
  toggleExpand: (id: string, e?: React.MouseEvent) => void;
  t: any;
  renderCategoryIcon: (cat: CategoryNode, iconSize?: string, containerSize?: string, bgColor?: string) => JSX.Element;
  counts: Record<string, number>;
  parentNames?: string[];
}

const CategoryItem: React.FC<CategoryItemProps> = ({
  node,
  level,
  selectedCategory,
  selectedSubCategory,
  selectedDetailCategory,
  handleCategorySelect,
  expandedCategories,
  toggleExpand,
  t,
  renderCategoryIcon,
  counts,
  parentNames = []
}) => {
  const { i18n: i18nFromHook } = useTranslation('common');
  const isMain = level === 0;
  
  // Check if this node is actively selected in the filter path
  const isSelected = isMain
    ? selectedCategory === node.id
    : level === 1
      ? selectedSubCategory === node.id
      : selectedDetailCategory === node.id;
  
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedCategories.has(node.id);
  const isRtl = i18nFromHook.language.startsWith('ar');
  const count = counts[node.id] || 0;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Apply filter for this node at any level
    if (isMain) {
      handleCategorySelect(isSelected ? FILTER_ALL : node.id);
    } else if (level === 1) {
      handleCategorySelect(selectedCategory, isSelected ? undefined : node.id);
    } else if (level === 2) {
      handleCategorySelect(selectedCategory, selectedSubCategory, isSelected ? undefined : node.id);
    } else {
      handleCategorySelect(selectedCategory, selectedSubCategory, selectedDetailCategory, node.id);
    }
    
    // Toggle accordion expand for parent nodes
    if (hasChildren) {
      toggleExpand(node.id, e);
    }
  };

  return (
    <div className="space-y-0.5">
      <button
        onClick={handleClick}
        className={`w-full flex items-center justify-between transition-all duration-300 group ${
          isMain 
            ? `p-2 rounded-xl ${isSelected ? 'bg-primary-50 dark:bg-slate-800 text-primary-600 dark:text-primary-400 shadow-sm ring-1 ring-primary-200 dark:ring-slate-700' : 'text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800/60 hover:shadow-md dark:hover:shadow-black/10'}`
            : `py-1.5 px-2 text-xs font-semibold ${isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-slate-500 dark:text-slate-400 hover:text-primary-500 dark:hover:text-primary-400'}`
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {renderCategoryIcon(node, isMain ? "w-4 h-4" : "w-3.5 h-3.5", isMain ? "w-7 h-7" : "w-6 h-6", isMain ? undefined : "bg-slate-100 dark:bg-slate-800")}
          <span className={`${isMain ? 'font-bold text-[13px] tracking-wide' : ''} truncate flex-1 text-start`}>
            {formatCategoryTreeLabel(node, i18nFromHook.language, t)}
          </span>
          {count > 0 && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
              isSelected ? 'bg-primary-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
            }`}>
              {count}
            </span>
          )}
        </div>
        {hasChildren && (
          <div className="flex items-center gap-1">
             {isExpanded ? <ChevronDown className="w-4 h-4 text-primary-500" /> : (isRtl ? <ChevronRight className="w-4 h-4 rotate-180 opacity-40 group-hover:opacity-100" /> : <ChevronRight className="w-4 h-4 opacity-40 group-hover:opacity-100" />)}
          </div>
        )}
      </button>

      {isExpanded && hasChildren && (
        <div className={`${isRtl ? 'mr-5 border-r-2 border-slate-200 dark:border-slate-700/60 pr-2' : 'ml-5 border-l-2 border-slate-200 dark:border-slate-700/60 pl-2'} space-y-0.5 mt-1 animate-fadeIn`}>
          {/* View All Button for the current Parent Category */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isMain) handleCategorySelect(node.id);
              else if (level === 1) handleCategorySelect(selectedCategory, node.id);
              else if (level === 2) handleCategorySelect(selectedCategory, selectedSubCategory, node.id);
            }}
            className={`w-full flex items-center gap-2 py-1.5 px-2 text-xs font-semibold rounded-lg transition-colors ${
              isSelected && 
              ((isMain && !selectedSubCategory) || 
               (level === 1 && !selectedDetailCategory))
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                : 'text-slate-500 dark:text-slate-400 hover:text-primary-500 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            <div className="w-2 h-2 rounded-full bg-current opacity-50 shrink-0" />
            <span className="text-start flex-1 truncate">{t('home.filters.all')} {formatCategoryTreeLabel(node, i18nFromHook.language, t)}</span>
          </button>

          {node.children.map((child) => (
            <CategoryItem 
              key={child.id} 
              node={child} 
              level={level + 1}
              selectedCategory={selectedCategory}
              selectedSubCategory={selectedSubCategory}
              selectedDetailCategory={selectedDetailCategory}
              handleCategorySelect={handleCategorySelect}
              expandedCategories={expandedCategories}
              toggleExpand={toggleExpand}
              t={t}
              renderCategoryIcon={renderCategoryIcon}
              counts={counts}
              parentNames={[...parentNames, node.name]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SparklesIcon = ({ className }: { className: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

export default HierarchicalFilter;
